import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2, ChevronDown, ChevronUp, Clock, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/features/LoadingSpinner'
import type { PaymentTaskDto, TasksResponse } from '@/types'
import { useNotBilledDecision } from '@/hooks/useNotBilledDecision'
import { FreeTouchUpDialog } from '@/components/features/invoicing/FreeTouchUpDialog'
import { DisabledHint } from '@/components/ui/disabled-hint'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'

function formatAmount(amount: number): string {
  return amount.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' })
}

function formatRelativeDate(iso: string): string {
  const date = new Date(iso)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  date.setHours(0, 0, 0, 0)
  const diffDays = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return "Aujourd'hui"
  if (diffDays === 1) return 'Demain'
  if (diffDays === -1) return 'Hier'
  if (diffDays > 0) return `Dans ${diffDays}j`
  return `Il y a ${Math.abs(diffDays)}j`
}

function formatAbsoluteDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' })
}

interface TaskRowProps {
  task: PaymentTaskDto
  onGenerateInvoice: (task: PaymentTaskDto) => void
  onFreeTouchUp: (task: PaymentTaskDto) => void
  isDecisionPending: boolean
}

function TaskRow({ task, onGenerateInvoice, onFreeTouchUp, isDecisionPending }: TaskRowProps) {
  const absoluteDate = formatAbsoluteDate(task.triggerDate)
  const { can, reasonIfDenied } = usePermissions()
  // SOLY-REGIE-002 — la première ligne dont le montant est INCONNU. On lit le drapeau, jamais
  // `amountToInvoice === 0` : une facture supplémentaire à 0 € existe (SOLY-INV-003).
  const unknownAmount = task.amountUnknown

  return (
    <div
      className={cn(
        'group flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors border-l-2',
        task.isOverdue ? 'border-l-destructive' : 'border-l-transparent'
      )}
      data-testid="task-card"
    >
      {/* Main info */}
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm truncate block">{task.clientName}</span>
        <p className="text-xs text-muted-foreground mt-0.5 truncate">
          {task.stepName}{task.taskType === 'payment' ? ` ${task.percentage}%` : ''}
          {' · '}
          <Link
            to={`/projects/${task.projectId}`}
            className="hover:underline underline-offset-2 hover:text-foreground transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {task.projectName}
          </Link>
          {!unknownAmount && <span className="sm:hidden"> · {formatAmount(task.amountToInvoice)}</span>}
        </p>
      </div>

      {/* Amount + context (desktop) */}
      <div className="text-right shrink-0 hidden sm:block">
        {/* La cellule reste VIDE, jamais « 0,00 € » ni « — » : un zéro affirmerait que le travail
            vaut zéro. Même décision que le PDF du lot 2. La date, elle, reste — c'est le fait. */}
        {!unknownAmount && (
          <p className="text-sm font-semibold tabular-nums">{formatAmount(task.amountToInvoice)}</p>
        )}
        {unknownAmount ? (
          <p className="text-xs text-muted-foreground" title={absoluteDate} data-testid="task-amount-unknown">
            Effectué le {absoluteDate}
          </p>
        ) : task.amountToInvoice < task.suggestedAmount ? (
          <p className="text-xs text-muted-foreground">
            {formatAmount(task.suggestedAmount)} · {task.percentageAlreadyInvoiced.toFixed(1)}% déjà facturé
          </p>
        ) : task.amountToInvoice > task.suggestedAmount ? (
          <p className="text-xs text-muted-foreground">
            Étape {formatAmount(task.suggestedAmount)} · +{formatAmount(task.amountToInvoice - task.suggestedAmount)} rattrapage
          </p>
        ) : (
          <p className="text-xs text-muted-foreground" title={absoluteDate}>
            Éch. {absoluteDate}
          </p>
        )}
      </div>

      {/* CTA */}
      {unknownAmount ? (
        <div className="flex shrink-0 items-center gap-2">
          {/* Le title va sur le wrapper : `disabled:pointer-events-none` rendrait le bouton grisé
              muet au survol (défaut 🔴-2 de la QA du 20/08). */}
          <DisabledHint hint={reasonIfDenied(PERMISSIONS.INVOICE_WRITE)}>
            <Button
              size="sm"
              className="h-7 px-2.5 text-xs"
              disabled={!can(PERMISSIONS.INVOICE_WRITE)}
              onClick={() => onGenerateInvoice(task)}
              data-testid="task-define-price-btn"
            >
              Définir le prix
            </Button>
          </DisabledHint>
          <DisabledHint hint={reasonIfDenied(PERMISSIONS.INVOICE_WRITE)}>
            <button
              type="button"
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:no-underline"
              disabled={isDecisionPending || !can(PERMISSIONS.INVOICE_WRITE)}
              onClick={() => onFreeTouchUp(task)}
              data-testid="task-free-touch-up-btn"
            >
              Retouche gratuite
            </button>
          </DisabledHint>
        </div>
      ) : (
        <Button
          size="sm"
          className="shrink-0 h-7 px-2.5 text-xs"
          onClick={() => onGenerateInvoice(task)}
          data-testid="task-generate-invoice-btn"
        >
          Facturer
        </Button>
      )}
    </div>
  )
}

interface InboxSectionProps {
  label: string
  count: number
  variant?: 'overdue' | 'default'
  children: React.ReactNode
}

function InboxSection({ label, count, variant = 'default', children }: InboxSectionProps) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-2.5 border-b',
          variant === 'overdue' ? 'bg-destructive/10' : 'bg-muted/50'
        )}
      >
        {variant === 'overdue' && <AlertCircle className="size-3.5 text-destructive shrink-0" />}
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-wide',
            variant === 'overdue' ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            'ml-1 text-xs font-medium tabular-nums',
            variant === 'overdue' ? 'text-destructive/70' : 'text-muted-foreground/70'
          )}
        >
          {count}
        </span>
      </div>
      <div className="divide-y">{children}</div>
    </div>
  )
}

interface FutureTaskRowProps {
  task: PaymentTaskDto
}

function FutureTaskRow({ task }: FutureTaskRowProps) {
  const relativeDate = formatRelativeDate(task.triggerDate)
  const absoluteDate = formatAbsoluteDate(task.triggerDate)

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-l-2 border-l-transparent">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground truncate">{task.clientName}</p>
        <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{task.stepName}{task.taskType === 'payment' ? ` ${task.percentage}%` : ''}</p>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        {!task.amountUnknown && (
          <p className="text-sm tabular-nums text-muted-foreground">{formatAmount(task.amountToInvoice)}</p>
        )}
        <p className="text-xs text-muted-foreground/70" title={absoluteDate}>{relativeDate}</p>
      </div>
      <p className="text-xs text-muted-foreground sm:hidden shrink-0" title={absoluteDate}>{relativeDate}</p>
    </div>
  )
}

interface InvoicesTodoTabProps {
  data: TasksResponse | null
  loading: boolean
  error: string | null
  /** Recharge la liste après une décision — les tâches sont calculées serveur à chaque appel. */
  onRefresh?: () => void | Promise<void>
}

/**
 * "À facturer" tab of the invoices page: the invoicing worklist (payment
 * schedule steps due, direct sales to invoice, supplementary invoices) plus
 * the collapsed forecast of upcoming schedule steps. Tasks are computed
 * server-side on the fly; generating the suggested invoice makes the task
 * disappear on the next fetch.
 */
export function InvoicesTodoTab({ data, loading, error, onRefresh }: InvoicesTodoTabProps) {
  const navigate = useNavigate()
  const [showFuture, setShowFuture] = useState(false)
  const [freeTouchUpTarget, setFreeTouchUpTarget] = useState<PaymentTaskDto | null>(null)
  const { isPending: isDecisionPending, error: decisionError, markNotBilled } =
    useNotBilledDecision(async () => {
      setFreeTouchUpTarget(null)
      await onRefresh?.()
    })

  function handleGenerateInvoice(task: PaymentTaskDto) {
    // SOLY-REGIE-002 — branche EXPLICITE et en tête. Le `default` en bas route vers le générateur
    // d'échéance : une valeur non traitée y tomberait en silence, avec un montant de 0 € et un
    // type de facture qui ne veut rien dire. C'est le piège du « 6ᵉ kind » du lot 2.
    if (task.taskType === 'intervention_to_price') {
      // Pas `/projets/:id/invoices/direct` : il n'y a rien à facturer tant que rien n'est prisé.
      // On envoie là où le PRIX se tape.
      navigate(`/projects/${task.projectId}/commande-directe`)
      return
    }
    if (task.taskType === 'direct_sale_invoice') {
      navigate(`/projets/${task.projectId}/invoices/direct`)
      return
    }
    if (task.taskType === 'supplementary_invoice') {
      navigate(`/projets/${task.projectId}/invoices/standalone`)
      return
    }
    const params = new URLSearchParams({
      step: '2',
      invoice_type: task.invoiceType,
      amount: String(task.amountToInvoice),
    })
    navigate(`/projets/${task.projectId}/invoices/new?${params.toString()}`)
  }

  const overdueTasks = data?.activeTasks.filter((t) => t.isOverdue) ?? []
  const dueTasks = data?.activeTasks.filter((t) => !t.isOverdue) ?? []
  // ⚠️ Le filtre porte sur le DRAPEAU, jamais sur `amountToInvoice > 0` — une facture
  // supplémentaire à 0 € existe et doit rester comptée. Un total qui absorberait un montant
  // inconnu comme un zéro mentirait sur le reste à facturer.
  const totalActive =
    data?.activeTasks.filter((t) => !t.amountUnknown).reduce((sum, t) => sum + t.amountToInvoice, 0) ?? 0
  const activeCount = data?.activeTasks.length ?? 0

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div
        className="flex items-center gap-2 text-destructive text-sm p-4 rounded-lg border border-destructive/30 bg-destructive/5"
        data-testid="invoices-page-todo-error"
      >
        <AlertCircle className="size-4 shrink-0" />
        {error ?? 'Impossible de charger les tâches.'}
      </div>
    )
  }

  return (
    <div className="max-w-2xl space-y-4" data-testid="tasks-ready">
      {activeCount > 0 && (
        <div className="flex justify-end">
          <span className="text-sm text-muted-foreground">
            {activeCount} à traiter · {formatAmount(totalActive)}
          </span>
        </div>
      )}

      {/* Empty state */}
      {activeCount === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <CheckCircle2 className="size-12 text-green-500" />
          <p className="text-lg font-medium">Tout est à jour.</p>
          <p className="text-sm text-muted-foreground">Aucune échéance en attente.</p>
        </div>
      )}

      {/* Overdue section */}
      {overdueTasks.length > 0 && (
        <InboxSection label="En retard" count={overdueTasks.length} variant="overdue">
          {overdueTasks.map((task) => (
            <TaskRow
              key={task.paymentScheduleStepId}
              task={task}
              onGenerateInvoice={handleGenerateInvoice}
              onFreeTouchUp={setFreeTouchUpTarget}
              isDecisionPending={isDecisionPending}
            />
          ))}
        </InboxSection>
      )}

      {/* Due section */}
      {dueTasks.length > 0 && (
        <InboxSection label="À facturer" count={dueTasks.length}>
          {dueTasks.map((task) => (
            <TaskRow
              key={task.paymentScheduleStepId}
              task={task}
              onGenerateInvoice={handleGenerateInvoice}
              onFreeTouchUp={setFreeTouchUpTarget}
              isDecisionPending={isDecisionPending}
            />
          ))}
        </InboxSection>
      )}

      {/* Future tasks */}
      {data.futureTasks.length > 0 && (
        <div>
          <button
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
            onClick={() => setShowFuture((v) => !v)}
            data-testid="toggle-future-tasks-btn"
          >
            <Clock className="size-4" />
            {data.futureTasks.length} échéance{data.futureTasks.length > 1 ? 's' : ''} à venir
            {showFuture ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </button>

          {showFuture && (
            <div className="mt-2 rounded-lg border overflow-hidden">
              <div className="divide-y">
                {data.futureTasks.map((task) => (
                  <FutureTaskRow key={task.paymentScheduleStepId} task={task} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {decisionError && (
        <p className="text-xs text-destructive" data-testid="task-decision-error">
          {decisionError}
        </p>
      )}

      <FreeTouchUpDialog
        open={freeTouchUpTarget !== null}
        onOpenChange={(next) => !next && setFreeTouchUpTarget(null)}
        isPending={isDecisionPending}
        projectName={freeTouchUpTarget?.projectName}
        onConfirm={() => freeTouchUpTarget && markNotBilled(freeTouchUpTarget.projectId)}
      />
    </div>
  )
}
